import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize2, ExternalLink, Settings } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getApiBaseUrl, HLS_LIVE_CONFIG } from '../utils/hlsPlayer';
import './MatchStreamPlayer.css';

// HLS is loaded globally from CDN in index.html
const Hls = window.Hls;

const MatchStreamPlayer = ({ matchStreams, matchInfo }) => {
  const { theme } = useTheme();

  // Get language preference from cookies, default to first available language
  const getLanguageFromCookies = () => {
    const cookies = document.cookie.split(';');
    const langCookie = cookies.find(cookie => cookie.trim().startsWith('preferredLanguage='));
    if (langCookie) {
      return langCookie.split('=')[1];
    }
    // Default to English if available, otherwise first available language
    if (matchStreams?.byLanguage?.['English']) {
      return 'English';
    }
    return Object.keys(matchStreams?.byLanguage || {})[0] || 'English';
  };

  const [selectedLanguage, setSelectedLanguage] = useState(getLanguageFromCookies());
  const [currentStream, setCurrentStream] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [error, setError] = useState(null);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [isLiveSync, setIsLiveSync] = useState(false);
  
  const videoRef = useRef(null);
  const playerContainerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [videoElementReady, setVideoElementReady] = useState(false);

  // Get available languages from actual channel data
  const getAvailableLanguages = () => {
    if (!matchStreams?.byLanguage) return [];

    // Get channel data with logos from provider
    return Object.keys(matchStreams.byLanguage).map(channelName => {
      // Get the first stream to extract channel info (all streams for same channel should have same logo)
      const streams = matchStreams.byLanguage[channelName];
      const firstStream = streams?.[0];

      // Use logo from provider data, fallback to default TV icon
      const logo = firstStream?.logo || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMiIgeT0iMyIgd2lkdGg9IjIwIiBoZWlnaHQ9IjE0IiByeD0iMiIgcnk9IjIiIHN0cm9rZT0iIzY2NjY2NiIgc3Ryb2tlLXdpZHRoPSIyIiBmaWxsPSIjZjNmNGY2Ii8+CjxwYXRoIGQ9Im0xNyAyMSA0LTQtNC00IiBzdHJva2U9IiM2NjY2NjYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=';

      return {
        key: channelName,
        name: channelName.length > 15 ? channelName.substring(0, 15) + '...' : channelName,
        logo: logo,
        icon: '📺' // Keep as fallback
      };
    });
  };

  const languages = getAvailableLanguages();

  // Get language prefix for channel ID mapping
  const getLanguagePrefix = (language) => {
    const langMap = {
      'English': 'en',
      'Arabic': 'ar',
      'Spanish': 'es',
      'French': 'fr', 
      'German': 'de',
      'Italian': 'it',
      'Portuguese': 'pt',
      'Turkish': 'tr',
      'Dutch': 'nl',
      'Polish': 'pl',
      'Russian': 'ru',
      'Other': 'other'
    };
    return langMap[language] || 'en';
  };

  // Auto-select first channel when matchStreams changes
  useEffect(() => {
    if (matchStreams?.byLanguage) {
      const availableChannels = Object.keys(matchStreams.byLanguage);
      if (availableChannels.length > 0 && !availableChannels.includes(selectedLanguage)) {
        setSelectedLanguage(availableChannels[0]);
        console.log(`🔄 Auto-selected first channel: ${availableChannels[0]}`);
      }
    }
  }, [matchStreams, selectedLanguage]);

  // Get streams for selected language
  const availableStreams = React.useMemo(() =>
    matchStreams?.byLanguage?.[selectedLanguage] || [],
    [matchStreams, selectedLanguage]
  );

  // Auto-select HD quality channel for selected language (HD-first priority)
  useEffect(() => {
    console.log(`🔍 Available streams for ${selectedLanguage}:`, availableStreams);
    if (availableStreams.length > 0) {
      // PRIORITY: HD first (best balance), then FHD, SD, 4K last
      const qualityOrder = { 'HD': 0, 'FHD': 1, 'SD': 2, '4K': 3 };
      const sortedStreams = [...availableStreams].sort((a, b) => {
        const qualityA = qualityOrder[a.quality] ?? 4;
        const qualityB = qualityOrder[b.quality] ?? 4;
        return qualityA - qualityB;
      });
      console.log(`🔍 Selected stream:`, sortedStreams[0]);
      setCurrentStream(sortedStreams[0]); // Always auto-select HD (best performance)
    } else {
      setCurrentStream(null);
    }
  }, [selectedLanguage, matchStreams, availableStreams]);

  // Reset video element ready state when current stream changes
  useEffect(() => {
    if (currentStream && !videoElementReady) {
      console.log('🔄 New stream selected, ensuring video element is ready');
      // Force check video element readiness
      if (videoRef.current) {
        setVideoElementReady(true);
        setVideoReady(true);
      }
    }
  }, [currentStream, videoElementReady]);

  // Store HLS instance for cleanup
  const hlsRef = useRef(null);

  // Track video element readiness with proper lifecycle
  useEffect(() => {
    const video = videoRef.current;
    if (video && !videoElementReady) {
      console.log('📺 Video element found, checking readiness...');
      
      // Check if video element is already ready
      if (video.readyState >= 0) {
        console.log('✅ Video element is immediately ready');
        setVideoElementReady(true);
        setVideoReady(true);
      } else {
        // Wait for video element to be fully ready
        const handleLoadedMetadata = () => {
          console.log('✅ Video element metadata loaded');
          setVideoElementReady(true);
          setVideoReady(true);
        };
        
        const handleCanPlay = () => {
          console.log('✅ Video element can play');
          setVideoElementReady(true);
          setVideoReady(true);
        };
        
        video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
        video.addEventListener('canplay', handleCanPlay, { once: true });
        
        // Cleanup function
        return () => {
          video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          video.removeEventListener('canplay', handleCanPlay);
        };
      }
    }
  }, [videoElementReady]);

  // Load stream when current stream changes - Simplified approach
  useEffect(() => {
    if (!currentStream) return;
    
    console.log('🔄 Loading new stream:', currentStream.name);
    
    // Cleanup previous HLS instance first
    if (hlsRef.current) {
      console.log('🧹 Cleaning up previous HLS instance');
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    
    // Simple direct approach - just try to load immediately
    const loadWithRetry = (retryCount = 0) => {
      if (!videoRef.current) {
        if (retryCount < 20) { // 2 seconds max
          console.log(`⏳ Video element not ready, retry ${retryCount + 1}/20`);
          setTimeout(() => loadWithRetry(retryCount + 1), 100);
        } else {
          console.error('❌ Video element never became available');
          setError('Video player initialization failed');
        }
        return;
      }
      
      console.log('✅ Video element available, loading stream directly');
      loadStream(currentStream);
    };
    
    // Start immediately, no delays
    loadWithRetry();
    
    // Cleanup function
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [currentStream]);

  // Get multiplexed streaming URLs 
  const getStreamUrls = (stream) => {
    // Debug the stream object structure
    console.log(`🔍 Stream object:`, stream);
    
    // Use the streamUrl which points to our API endpoint
    const url = stream.streamUrl || stream.url;
    console.log(`🎯 Using stream URL: ${url}`);

    // Handle undefined URL
    if (!url) {
      console.error('❌ No stream URL found in stream object:', stream);
      return {
        primary: null,
        channelId: 'error',
        isMultiplexed: false,
        hasFallback: false
      };
    }

    // Convert relative URL to absolute URL
    const API_BASE_URL = getApiBaseUrl();
    const absoluteUrl = url.startsWith('/') ? `${API_BASE_URL}${url}` : url;

    // Create HLS proxy URLs to solve CORS issues
    const getProxiedUrl = (originalUrl) => {
      const encodedUrl = encodeURIComponent(originalUrl);
      return `${API_BASE_URL}/api/hls/proxy/${encodedUrl}`;
    };

    // Determine if URL is already proxied by backend
    const isAlreadyProxied = absoluteUrl.includes('/api/hls/proxy/');
    const isBackendUrl = absoluteUrl.includes('/api/channels/') || absoluteUrl.includes('/api/stream/');
    const isDirectStreamUrl = !isAlreadyProxied && !isBackendUrl && (absoluteUrl.includes('.m3u8') || absoluteUrl.includes('get.php'));

    let primaryUrl, fallbackUrl;

    if (isAlreadyProxied) {
      // Already proxied by backend - use as is
      primaryUrl = absoluteUrl;
      fallbackUrl = absoluteUrl;
      console.log(`✅ Using backend-proxied stream: ${primaryUrl}`);
    } else if (isDirectStreamUrl) {
      // Direct stream URL - must be proxied for CORS
      primaryUrl = getProxiedUrl(absoluteUrl);
      fallbackUrl = getProxiedUrl(absoluteUrl); // Same for fallback
      console.log(`🔄 Using HLS proxy for direct stream: ${absoluteUrl} -> ${primaryUrl}`);
    } else {
      // API endpoint - use as is (backend should handle CORS)
      primaryUrl = absoluteUrl;
      fallbackUrl = url.includes('/api/channel/')
        ? url.replace('/api/channel/', '/api/channel-alt/')
        : `${API_BASE_URL}/api/channel-alt/${encodeURIComponent(stream.name || stream.channel || stream.id || 'unknown')}`;
      console.log(`✅ Using API endpoint: ${primaryUrl}`);
    }

    return {
      primary: primaryUrl,
      fallback: fallbackUrl,
      channelId: stream.id || 'direct',
      channelName: stream.name || stream.channel || stream.id || 'unknown',
      isMultiplexed: true, // Back to using our streaming API
      hasFallback: true
    };
  };

  // Map stream to channel ID for multiplexing
  // eslint-disable-next-line no-unused-vars
  const getChannelIdForStreaming = (stream) => {
    const url = stream.url || stream.originalUrl || '';
    console.log(`🔍 Debug channel mapping:`, { stream, url, selectedLanguage });
    
    // First check if this is already a streaming API URL
    if (url.includes('/api/channel/')) {
      const channelIdMatch = url.match(/\/api\/channel\/([^/?]+)/);
      if (channelIdMatch) {
        const channelId = channelIdMatch[1];
        console.log(`✅ Direct channel ID from streaming URL: ${channelId}`);
        return channelId;
      }
    }
    
    // Also check if stream object has id property (new channel structure)
    if (stream.id) {
      // The stream.id should already be the correct format (e.g., "bein-sports-1-ar")
      console.log(`✅ Direct channel ID from stream object: ${stream.id}`);
      return stream.id;
    }
    
    let actualUrl = url;
    
    // If URL is HLS proxy format, decode the original URL
    if (url.includes('api/hls-proxy?url=')) {
      const encodedUrl = url.split('api/hls-proxy?url=')[1];
      actualUrl = decodeURIComponent(encodedUrl);
      console.log(`🔓 Decoded original URL: ${actualUrl}`);
    }
    
    // Extract provider ID from x96.pro URLs (legacy support)
    const providerIdMatch = actualUrl.match(/\/([0-9]+)$/);
    if (!providerIdMatch) {
      console.log(`❌ No provider ID found in URL: ${actualUrl}`);
      return null;
    }
    
    const providerId = providerIdMatch[1];
    console.log(`🔢 Extracted provider ID: ${providerId}`);
    
    // Map provider IDs to channel IDs with quality priority (always prefer HD channels)
    const providerToChannelMap = {
      // BeIN Sport 1 Arabic
      '61303': 'bein-sports-1-ar',     // BeIN Sport 1 Premium FHD
      '146390': 'bein-sports-1-ar',    // BeIN Sport 1 Premium FHD (Backup)
      '613342': 'bein-sports-1-ar',    // BeIN Sport 1 HD
      '109021': 'bein-sports-1-ar',    // BeIN Sport 1 SD
      
      // BeIN Sport 1 English
      '613317': 'bein-sports-1-en',    // BeIN Sport 1 English FHD
      
      // BeIN Sport Turkey
      '829380': 'bein-sports-turkey-1-tr', // BeIN Sports Turkey 1 FHD
      '97583': 'bein-sports-turkey-1-tr',  // BeIN Sports Turkey 1 HD
      
      // German DAZN channels
      '915455': 'sky-sport-bundesliga-1', // Sky Sport Bundesliga 1 HD
      '915464': 'sky-sport-bundesliga-1', // Sky Sport Bundesliga 1 HD (Backup)
      '915465': 'dazn-bar-2',            // DAZN Bar 2 HD
      
      // Additional provider mappings for diversity
    };
    
    const streamQuality = stream.quality || 'HD';
    console.log(`📊 Stream quality: ${streamQuality} for provider ${providerId}`);
    
    const channelId = providerToChannelMap[providerId];
    if (channelId) {
      console.log(`📺 Mapped provider ${providerId} -> channel ${channelId}`);
      return channelId;
    }
    
    // Generic mapping based on channel name with correct ID format
    const channelName = stream.name?.toLowerCase() || '';
    const languageCode = getLanguagePrefix(selectedLanguage);
    
    if (channelName.includes('bein') && channelName.includes('sport')) {
      // Try to map to specific BeIN Sport channels based on name
      if (channelName.includes('turkey')) return `bein-sports-turkey-1-${languageCode}`;
      if (channelName.includes('english')) return `bein-sports-1-${languageCode}`;
      if (channelName.includes('1')) return `bein-sports-1-${languageCode}`;
      if (channelName.includes('2')) return `bein-sports-2-${languageCode}`;
      if (channelName.includes('3')) return `bein-sports-3-${languageCode}`;
      if (channelName.includes('4')) return `bein-sports-4-${languageCode}`;
      if (channelName.includes('5')) return `bein-sports-5-${languageCode}`;
      if (channelName.includes('6')) return `bein-sports-6-${languageCode}`;
      if (channelName.includes('7')) return `bein-sports-7-${languageCode}`;
      return `bein-sports-1-${languageCode}`; // Default to BeIN Sport 1
    }
    if (channelName.includes('dazn')) {
      if (channelName.includes('bar')) return `dazn-bar-2`; // DAZN Bar channels
      if (channelName.includes('1')) return `dazn-1-${languageCode}`;
      if (channelName.includes('2')) return `dazn-2-${languageCode}`;
      return `dazn-1-${languageCode}`; // Default DAZN
    }
    if (channelName.includes('sky')) {
      if (channelName.includes('bundesliga')) return `sky-sport-bundesliga-1`;
      if (channelName.includes('premier')) return `sky-sports-premier-league-${languageCode}`;
      if (channelName.includes('football')) return `sky-sports-football-${languageCode}`;
      return `sky-sports-football-${languageCode}`; // Default Sky Sports
    }
    
    console.warn(`⚠️ No channel mapping for provider ${providerId}, name: ${channelName}`);
    return null;
  };



  const loadStream = async (stream) => {
    console.log(`🎥 Loading stream: ${stream.name}`);
    setError(null);
    setIsPlaying(false);

    if (!stream || !videoRef.current) {
      console.log('❌ Cannot load stream: missing stream or video element');
      setError('Stream initialization failed');
      return;
    }

    // Reset video element
    videoRef.current.pause();
    videoRef.current.src = '';
    videoRef.current.load();

    // Check if streamUrl is an API endpoint that needs to be fetched
    let actualStreamUrl = stream.streamUrl || stream.url;

    if (actualStreamUrl && actualStreamUrl.startsWith('/api/stream/')) {
      console.log('🔍 Fetching actual stream URL from API...');
      try {
        const API_BASE_URL = getApiBaseUrl();
        const response = await fetch(`${API_BASE_URL}${actualStreamUrl}`);
        const data = await response.json();

        if (data.success && data.stream && data.stream.url) {
          actualStreamUrl = data.stream.url;
          console.log(`✅ Got actual stream URL: ${actualStreamUrl}`);
        } else {
          throw new Error(data.error || 'Failed to get stream URL');
        }
      } catch (err) {
        console.error('❌ Failed to fetch stream URL:', err);
        setError(`Failed to load stream: ${err.message}`);
        return;
      }
    }

    // Update stream object with actual URL
    const streamWithActualUrl = {
      ...stream,
      streamUrl: actualStreamUrl,
      url: actualStreamUrl
    };

    const streamUrls = getStreamUrls(streamWithActualUrl);
    console.log(`🎯 Stream URLs:`, streamUrls);

    // Load HLS stream using HLS.js with shared live streaming config
    if (Hls.isSupported()) {
      const hls = new Hls({
        ...HLS_LIVE_CONFIG,
        xhrSetup: function(xhr, url) {
          // Add IPTV provider authentication headers
          xhr.setRequestHeader('User-Agent', 'IPTV Smarters Pro/3.0.9.4 (Linux; Android 11)');
          xhr.setRequestHeader('Referer', url.split('/live/')[0] || url);
          xhr.setRequestHeader('Origin', url.split('/live/')[0] || url);
          xhr.setRequestHeader('Accept', 'application/vnd.apple.mpegurl, application/x-mpegurl, */*');
        }
      });

      // Store HLS instance for cleanup
      hlsRef.current = hls;

      console.log('🔗 Loading HLS source:', streamUrls.primary);
      hls.loadSource(streamUrls.primary);
      hls.attachMedia(videoRef.current);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('✅ HLS manifest parsed for:', stream.name);
        console.log('📊 HLS levels:', hls.levels);
        console.log('📊 Video source:', videoRef.current?.src || 'No src');
        setError(null);
        
        // Handle autoplay restrictions by trying muted first
        const attemptPlay = async () => {
          console.log('🎬 Attempting to play stream...');
          
          // Wait for buffer to build up before playing (3 seconds)
          console.log('⏳ Building buffer before playback...');
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          if (!videoRef.current) {
            console.log('❌ Video element disappeared during play attempt');
            setError('Video player error - please refresh');
            return;
          }
          
          try {
            // First try normal autoplay
            console.log('🔊 Trying unmuted autoplay...');
            await videoRef.current.play();
            console.log('✅ Stream playing successfully with sound');
            setIsPlaying(true);
            setError(null);
          } catch (autoplayError) {
            console.log('🔇 Autoplay blocked, trying muted autoplay...', autoplayError.message);
            try {
              // Try muted autoplay as fallback
              videoRef.current.muted = true;
              setIsMuted(true);
              await videoRef.current.play();
              console.log('✅ Stream playing muted successfully');
              console.log('📊 Video state:', {
                currentTime: videoRef.current.currentTime,
                duration: videoRef.current.duration,
                readyState: videoRef.current.readyState,
                networkState: videoRef.current.networkState,
                paused: videoRef.current.paused,
                ended: videoRef.current.ended
              });
              setIsPlaying(true);
              setError('🔇 Stream started muted (autoplay restriction). Click volume to unmute.');
            } catch (mutedError) {
              console.log('❌ Both autoplay attempts failed:', mutedError.message);
              setError('Click play to start the stream');
              setIsPlaying(false);
            }
          }
        };
        
        attemptPlay();
      });
      
      // Enhanced Error Handling with 2025 Best Practices
      let networkRetryCount = 0;
      let mediaRetryCount = 0;
      const maxRetries = 5;

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('❌ HLS Error for', stream.name, ':', data);

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log(`🔄 Network error (retry ${networkRetryCount + 1}/${maxRetries}), attempting recovery...`);

              if (networkRetryCount < maxRetries) {
                networkRetryCount++;

                // Exponential backoff retry
                const retryDelay = Math.min(1000 * Math.pow(2, networkRetryCount - 1), 10000);
                console.log(`⏳ Retrying in ${retryDelay}ms...`);

                setTimeout(() => {
                  hls.startLoad();
                }, retryDelay);

                // Show user-friendly message during retry
                setError(`Connection issue - retrying (${networkRetryCount}/${maxRetries})...`);
              } else {
                // Max retries exceeded - try quality downgrade
                const currentLevel = hls.currentLevel;
                if (currentLevel > 0) {
                  console.log('🔽 Switching to lower quality due to network issues');
                  hls.currentLevel = currentLevel - 1;
                  networkRetryCount = 0; // Reset retry count for new quality
                  hls.startLoad();
                  setError('Switched to lower quality due to connection issues');
                } else {
                  // Try browser-compatible fallback stream
                  if (streamUrls.hasFallback && streamUrls.fallback) {
                    console.log('🌐 Switching to browser-compatible fallback stream...');
                    setError('Switching to browser-compatible stream...');
                    hls.loadSource(streamUrls.fallback);
                    networkRetryCount = 0; // Reset retry count for fallback
                  } else {
                    setError('Network error - stream temporarily unavailable');
                  }
                }
              }
              break;

            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log(`🔄 Media error (retry ${mediaRetryCount + 1}/${maxRetries}), attempting recovery...`);

              if (mediaRetryCount < maxRetries) {
                mediaRetryCount++;

                // Try media error recovery
                setTimeout(() => {
                  hls.recoverMediaError();
                }, 1000);

                setError(`Media issue - recovering (${mediaRetryCount}/${maxRetries})...`);
              } else {
                // Try browser-compatible fallback stream
                if (streamUrls.hasFallback && streamUrls.fallback) {
                  console.log('🌐 Media error - switching to browser-compatible fallback stream...');
                  setError('Switching to browser-compatible stream...');
                  hls.loadSource(streamUrls.fallback);
                  mediaRetryCount = 0; // Reset retry count for fallback
                } else {
                  setError('Media playback error - please try another stream');
                }
              }
              break;

            default:
              console.error('💥 Fatal HLS error:', data.details);

              // Check if it's a 503 error (channel unavailable from backend validation)
              if (data.response?.code === 503 || data.details === 'manifestLoadError') {
                const errorMessage = data.response?.data?.details || 'Channel temporarily unavailable. The stream provider may be offline.';
                console.log('🚫 Channel unavailable:', errorMessage);
                setError(`⚠️ ${errorMessage}`);
                setIsPlaying(false);
              }
              // Attempt to reload the stream as last resort
              else if (data.details === 'manifestParseError') {
                console.log('🔄 Manifest error - reloading stream...');
                setTimeout(() => {
                  hls.loadSource(streamUrls.primary);
                }, 2000);
                setError('Stream manifest error - reloading...');
              } else {
                setError(`Stream error: ${data.details || 'Unknown error'}`);
              }
              break;
          }
        } else {
          // Non-fatal errors - log but continue
          console.warn('⚠️ Non-fatal HLS error:', data.details);

          // Handle specific non-fatal errors
          if (data.details === 'fragLoadError') {
            console.log('🔄 Fragment load error - HLS will automatically retry');
          }
        }
      });

      // Reset retry counters on successful recovery
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        networkRetryCount = 0;
        mediaRetryCount = 0;
      });
      
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        console.log('📺 HLS media attached successfully');
      });
      
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      console.log('🍎 Using native HLS support');
      videoRef.current.src = streamUrls.primary;
      
      const handleLoadedData = async () => {
        console.log('✅ Native HLS loaded');
        setError(null);
        
        // Handle autoplay restrictions for native HLS
        try {
          await videoRef.current.play();
          setIsPlaying(true);
        } catch (autoplayError) {
          console.log('🔇 Native HLS autoplay blocked, trying muted...');
          try {
            videoRef.current.muted = true;
            setIsMuted(true);
            await videoRef.current.play();
            setIsPlaying(true);
            setError('🔇 Stream started muted (autoplay restriction). Click volume to unmute.');
          } catch (mutedError) {
            setError('Click play to start the stream');
          }
        }
      };
      
      const handleError = () => {
        setError('Stream failed to load');
      };
      
      videoRef.current.addEventListener('loadeddata', handleLoadedData, { once: true });
      videoRef.current.addEventListener('error', handleError, { once: true });
    }
  };

  // Load MPEG-TS stream with auto-retry and intelligent error handling
  const loadHlsStream = (url, channelInfo, retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2 seconds
    
    if (!videoRef.current) {
      setError('Video player not ready');
      return;
    }

    // Show loading state during retries
    if (retryCount > 0) {
      setError(`Connecting to stream... (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`);
    }

    try {
      // Clear any previous source
      videoRef.current.src = '';
      videoRef.current.load();
      
      const video = videoRef.current;
      let loadTimeout;
      
      // Success handler - stream loaded
      const handleSuccess = () => {
        clearTimeout(loadTimeout);
        setError(null);
        
        // Clean up event listeners
        video.removeEventListener('loadeddata', handleSuccess);
        video.removeEventListener('error', handleError);
        video.removeEventListener('loadstart', handleLoadStart);
        
        // Try to play
        video.play().then(() => {
          setIsPlaying(true);
        }).catch(() => {
          setError('Click play to start the stream');
        });
      };

      const handleError = (e) => {
        clearTimeout(loadTimeout);
        
        // Clean up event listeners
        video.removeEventListener('loadeddata', handleSuccess);
        video.removeEventListener('error', handleError);
        video.removeEventListener('loadstart', handleLoadStart);
        
        console.log(`❌ Stream error (attempt ${retryCount + 1}):`, e);
        
        // Auto-retry logic
        if (retryCount < MAX_RETRIES) {
          console.log(`🔄 Retrying in ${RETRY_DELAY/1000}s... (${retryCount + 1}/${MAX_RETRIES})`);
          setTimeout(() => {
            loadHlsStream(url, channelInfo, retryCount + 1);
          }, RETRY_DELAY);
          return;
        }
        
        // All retries failed, try HLS.js as fallback
        console.log('🔄 All retries failed, trying HLS.js fallback...');
        tryHlsLoading();
      };
      
      const handleLoadStart = () => {
        clearTimeout(loadTimeout);
        
        // Set timeout for loading with retry logic
        loadTimeout = setTimeout(() => {
          if (!video.readyState || video.readyState < 2) {
            console.log(`⏰ Loading timeout (attempt ${retryCount + 1})`);
            
            // Auto-retry on timeout
            if (retryCount < MAX_RETRIES) {
              console.log(`🔄 Retrying due to timeout... (${retryCount + 1}/${MAX_RETRIES})`);
              // Clean up listeners before retry
              video.removeEventListener('loadeddata', handleSuccess);
              video.removeEventListener('error', handleError);
              video.removeEventListener('loadstart', handleLoadStart);
              
              setTimeout(() => {
                loadHlsStream(url, channelInfo, retryCount + 1);
              }, RETRY_DELAY);
              return;
            }
            
            // All retries failed, try HLS.js
            tryHlsLoading();
          }
        }, 8000); // 8 second timeout
      };
      
      // Set up event listeners
      video.addEventListener('loadeddata', handleSuccess, { once: true });
      video.addEventListener('error', handleError, { once: true });
      video.addEventListener('loadstart', handleLoadStart, { once: true });
      
      // Set source and load
      video.src = url;
      video.load();

    } catch (err) {
      console.error(`❌ Error loading direct stream for ${channelInfo}:`, err);
      setError(`Stream failed: ${err.message}`);
    }

    // HLS.js fallback for MPEG-TS streams
    function tryHlsLoading(url) {
        if (Hls.isSupported()) {
          // Use shared live streaming config with minor adjustments for MPEG-TS
          const hlsConfig = {
            ...HLS_LIVE_CONFIG,
            manifestLoadingTimeOut: 20000,
            manifestLoadingMaxRetry: 5,
            segmentLoadingTimeOut: 15000
          };

        const hls = new window.Hls(hlsConfig);
        
        hls.loadSource(url);
        hls.attachMedia(videoRef.current);
      
        // Monitor HLS errors and stream health
        hls.on(Hls.Events.ERROR, (event, data) => {
          console.log('🚨 HLS Error:', data);
          
          // Handle fatal errors with auto-recovery
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log('🔄 Network error, attempting recovery...');
                setError('Connection lost, reconnecting...');
                // Try to recover automatically
                setTimeout(() => {
                  hls.startLoad();
                }, 2000);
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log('🔄 Media error, attempting recovery...');
                setError('Stream error, attempting recovery...');
                hls.recoverMediaError();
                break;
              default:
                console.log('🔄 Fatal error, trying channel fallback...');
                hls.destroy();
                // Try fallback to HLS.js
                hls.destroy();
                setError('Stream failed, please try another channel');
                break;
            }
          }
        });

        // Monitor stream end and restart
        hls.on(Hls.Events.LEVEL_UPDATED, (event, data) => {
          // Check if playlist indicates end (EXT-X-ENDLIST)
          if (data.details && data.details.endOfStream) {
            console.log('📺 Stream ended unexpectedly, attempting restart...');
            setError('Stream ended, reconnecting...');
            
            // Wait 3 seconds then retry the same stream (it might have restarted)
            setTimeout(() => {
              hls.destroy();
              loadHlsStream(url, channelInfo, 0); // Start fresh with retry count 0
            }, 3000);
          }
        });

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setError(null);
          
          setTimeout(async () => {
            if (videoRef.current) {
              try {
                await videoRef.current.play();
                setIsPlaying(true);
                setIsLiveSync(true);
              } catch (autoplayError) {
                console.log('🔇 HLS.js autoplay blocked, trying muted...');
                try {
                  videoRef.current.muted = true;
                  setIsMuted(true);
                  await videoRef.current.play();
                  setIsPlaying(true);
                  setIsLiveSync(true);
                  setError('🔇 Stream started muted (autoplay restriction). Click volume to unmute.');
                } catch (mutedError) {
                  setError('Click play to start the stream');
                }
              }
            }
          }, 300);
        });
        
        // Monitor live position but let HLS.js handle seeking
        hls.on(Hls.Events.LEVEL_LOADED, () => {
          if (videoRef.current && hls.liveSyncPosition !== undefined) {
            const liveEdge = hls.liveSyncPosition;
            const currentTime = videoRef.current.currentTime;
            const timeDiff = liveEdge - currentTime;
            
            // Only update live sync status, don't force seeking
            if (timeDiff < 10) {
              setIsLiveSync(true);
            } else {
              setIsLiveSync(false);
            }
            
            // Log position for debugging without forcing seeks
            console.log(`📍 Live position: current=${currentTime.toFixed(1)}s, live=${liveEdge.toFixed(1)}s, diff=${timeDiff.toFixed(1)}s`);
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            if (data.type === 'mediaError') {
              try {
                hls.recoverMediaError();
              } catch (err) {
                setError('Stream error - trying to recover...');
              }
            } else if (data.type === 'networkError') {
              setError('Connecting to stream...');
            } else {
              setError('Stream format not supported by browser');
            }
          }
        });
        
        hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
          const currentLevel = hls.levels[data.level];
          console.log(`📊 Quality switched to: ${currentLevel?.height || 0}p for ${channelInfo}`);
          console.log(`📊 Full level info:`, currentLevel);
        });
        
        } else {
          throw new Error('HLS not supported in this browser');
        }
    } // End of tryHlsLoading function
  };

  const handlePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
    }
  };

  const switchStream = (stream) => {
    setCurrentStream(stream);
  };

  // Get available qualities for current language
  const getAvailableQualities = () => {
    if (!availableStreams.length) return [];
    
    const qualityGroups = {};
    availableStreams.forEach(stream => {
      if (!qualityGroups[stream.quality]) {
        qualityGroups[stream.quality] = [];
      }
      qualityGroups[stream.quality].push(stream);
    });

    // Sort by quality preference: HD > FHD > SD > 4K
    const qualityOrder = { 'HD': 0, 'FHD': 1, 'SD': 2, '4K': 3 };
    return Object.entries(qualityGroups).sort(([a], [b]) => {
      return (qualityOrder[a] ?? 4) - (qualityOrder[b] ?? 4);
    });
  };

  const availableQualities = getAvailableQualities();

  // Save language preference to cookies
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    document.cookie = `preferredLanguage=${language}; path=/; max-age=${60*60*24*30}`; // 30 days
  };


  return (
    <div className="match-stream-player" ref={playerContainerRef}>

      {/* Language Filter Tabs */}
      <div className="language-filters">
        {languages.map(lang => {
          const hasStreams = matchStreams?.byLanguage?.[lang.key]?.length > 0;
          return (
            <button
              key={lang.key}
              className={`language-tab ${selectedLanguage === lang.key ? 'active' : ''} ${!hasStreams ? 'disabled' : ''}`}
              onClick={() => hasStreams && handleLanguageChange(lang.key)}
              disabled={!hasStreams}
            >
              <span className="language-name">{lang.name}</span>
            </button>
          );
        })}
      </div>

      {/* Video Player */}
      <div className="video-container" ref={videoContainerRef}>
        {(!matchStreams || Object.keys(matchStreams.byLanguage || {}).length === 0) ? (
          <div className="stream-error">
            <div className="error-content">
              {(() => {
                let message = {
                  icon: '📺',
                  title: 'No Streams Available',
                  text: 'Sorry, there are no streams available for this match at the moment.'
                };

                // Custom messages based on match status
                if (matchInfo) {
                  if (matchInfo.status === 'finished') {
                    message = {
                      icon: '✅',
                      title: 'Match Ended',
                      text: `This match has finished. Final score: ${String(matchInfo.homeTeam || '')} ${String(matchInfo.homeScore || 0)} - ${String(matchInfo.awayScore || 0)} ${String(matchInfo.awayTeam || '')}`
                    };
                  } else if (matchInfo.status === 'scheduled') {
                    try {
                      const matchDate = new Date(matchInfo.date);
                      const dateStr = matchDate.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                      message = {
                        icon: '⏰',
                        title: 'Match Not Started',
                        text: `This match is scheduled for ${dateStr}`
                      };
                    } catch (e) {
                      message = {
                        icon: '⏰',
                        title: 'Match Not Started',
                        text: 'This match is scheduled for later'
                      };
                    }
                  } else if (matchInfo.status === 'live' || matchInfo.isLive) {
                    message = {
                      icon: '🔴',
                      title: 'No Stream Available',
                      text: `Match is LIVE (${String(matchInfo.minute || '0')}') but no streams are available yet.`
                    };
                  }
                }
                return (
                  <>
                    <div className="error-icon">{message.icon}</div>
                    <h3>{message.title}</h3>
                    <p>{message.text}</p>
                  </>
                );
              })()}
            </div>
          </div>
        ) : error ? (
          <div className="stream-error">
            <div className="error-content">
              <div className="error-icon">⚠️</div>
              <h3>Stream Error</h3>
              <p>{error}</p>
              <div className="error-actions">
                <button onClick={() => loadStream(currentStream)}>
                  Retry Stream
                </button>
                {availableStreams.length > 1 && (
                  <button onClick={() => {
                    const nextIndex = availableStreams.findIndex(s => s.id === currentStream.id) + 1;
                    if (nextIndex < availableStreams.length) {
                      switchStream(availableStreams[nextIndex]);
                    }
                  }}>
                    Try Next Stream
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              className="video-element"
              controls={false}
              onLoadStart={() => {
                console.log('🎬 Video onLoadStart event');
                setVideoElementReady(true);
                setVideoReady(true);
              }}
              onLoadedMetadata={() => {
                console.log('📹 Video metadata loaded');
                setVideoElementReady(true);
                setVideoReady(true);
              }}
              onCanPlay={() => {
                console.log('▶️ Video can play');
                setVideoElementReady(true);
                setVideoReady(true);
              }}
            />

            {/* Player Controls */}
            <div className="player-controls">
              <div className="control-bar">
                {/* Left controls */}
                <div className="controls-left">
                  <button className="control-btn" onClick={handlePlay}>
                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                  </button>
                  
                  <div className="volume-control">
                    <button className="control-btn" onClick={toggleMute}>
                      {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={volume}
                      onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                      className="volume-slider"
                    />
                  </div>

                  {/* Current Stream Info */}
                  {currentStream && (
                    <div className="stream-info">
                      <span className="stream-name">{currentStream.name}</span>
                      <span className="stream-quality">{currentStream.quality}</span>
                    </div>
                  )}
                </div>

                {/* Right controls */}
                <div className="controls-right">
                  {/* Quality Settings */}
                  {availableQualities.length > 1 && (
                    <div className="quality-settings">
                      <button 
                        className="control-btn"
                        onClick={() => setShowQualityMenu(!showQualityMenu)}
                        title="Quality Settings"
                      >
                        <Settings size={20} />
                      </button>
                      
                      {showQualityMenu && (
                        <div className="quality-menu">
                          <div className="quality-menu-header">
                            <span>Quality Settings</span>
                          </div>
                          <div className="quality-options">
                            {availableQualities.map(([quality, streams]) => (
                              <div key={quality} className="quality-group">
                                <div className="quality-label">{quality}</div>
                                {streams.map((stream, index) => (
                                  <button
                                    key={stream.id}
                                    className={`quality-option ${currentStream?.id === stream.id ? 'active' : ''}`}
                                    onClick={() => {
                                      switchStream(stream);
                                      setShowQualityMenu(false);
                                    }}
                                  >
                                    <span className="stream-name">{stream.name}</span>
                                    <span className="stream-quality-badge">{quality}</span>
                                  </button>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* External Link */}
                  {currentStream && (
                    <a
                      href={currentStream.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="control-btn"
                      title="Open in new tab"
                    >
                      <ExternalLink size={20} />
                    </a>
                  )}

                  {/* Picture-in-Picture */}
                  <button
                    className="control-btn"
                    title="Picture-in-Picture"
                    onClick={async () => {
                      try {
                        if (document.pictureInPictureElement) {
                          await document.exitPictureInPicture();
                        } else if (videoRef.current && document.pictureInPictureEnabled) {
                          await videoRef.current.requestPictureInPicture();
                        }
                      } catch (e) {
                        console.warn('PiP not available:', e);
                      }
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 7v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
                      <rect x="13" y="11" width="7" height="6" rx="1" fill="currentColor" />
                    </svg>
                  </button>

                  {/* Fullscreen */}
                  <button className="control-btn" onClick={() => {
                    if (videoRef.current) {
                      if (videoRef.current.requestFullscreen) {
                        videoRef.current.requestFullscreen();
                      } else if (videoRef.current.webkitRequestFullscreen) {
                        videoRef.current.webkitRequestFullscreen();
                      } else if (videoRef.current.msRequestFullscreen) {
                        videoRef.current.msRequestFullscreen();
                      } else if (videoRef.current.mozRequestFullScreen) {
                        videoRef.current.mozRequestFullScreen();
                      }
                    }
                  }}>
                    <Maximize2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Auto-playing info - no manual stream selection */}
      {currentStream && (
        <div className="auto-play-info">
          <div className="playing-info">
            <span className={`live-indicator ${isLiveSync ? 'live-sync' : 'buffering'}`}>
              <span className={isLiveSync ? 'player-live-dot' : 'player-buffer-dot'}></span>
            </span>
            <span>
              {isLiveSync 
                ? `LIVE - Auto-playing best quality stream in ${selectedLanguage}` 
                : `Syncing to live... Auto-playing best quality stream in ${selectedLanguage}`
              }
            </span>
            <div className="current-stream-details">
              <span className="stream-name">{currentStream.name}</span>
              <span className="stream-quality">{currentStream.quality}</span>
              {isLiveSync && <span className="live-badge">LIVE</span>}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MatchStreamPlayer;